// Money rules shared by the Reports page and the Office Today page, so both show the same totals.

export type BankTransactionRow = {
  id: string;
  unitId?: string | null;
  unitID?: string | null;
  unitNumber?: string | null;
  bookingId?: string | null;
  unitNo?: string | null;
  unit_no?: string | null;
  txnType?: string | null;
  receiptCategory?: string | null;
  paymentCategory?: string | null;
  receiptFor?: string | null;
  amount?: number | null;
  txnDate?: any;
  legacyChqDate?: string | null;
  legacyTransactionDate?: string | null;
  valueDate?: string | null;
  createdAt?: any;
  isActive?: boolean | null;
  excludeFromBookingReport?: boolean | null;
};

export type ActiveBookingUnit = { id: string; activeBookingId?: string | null };
export type BookingMeta = { bookingCreatedAtMs?: number; bookingHistoryCount?: number };

export const toMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0);
  if (typeof value?.seconds === 'number') return Number(value.seconds || 0) * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseDateLike = (value: any): Date | null => {
  if (!value && value !== 0) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof value?.toMillis === 'function') {
    const ms = Number(value.toMillis() || 0);
    if (ms > 0) {
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  if (typeof value?.seconds === 'number') {
    const d = new Date(Number(value.seconds || 0) * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = String(value || '').trim();
  if (!raw) return null;
  let m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getFinancialYearKey = (date: Date): string => {
  const startYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
};

export const getBookingPendingAmount = (unitAmount: number, receivedAmount: number): number =>
  Math.max(0, Number(unitAmount || 0) - Number(receivedAmount || 0));

export const resolveTxnDateLike = (txn: BankTransactionRow): Date | null => {
  const candidates = [txn.txnDate, txn.legacyChqDate, txn.legacyTransactionDate, txn.valueDate, txn.createdAt];
  for (const candidate of candidates) {
    const parsed = parseDateLike(candidate);
    if (parsed) return parsed;
  }
  return null;
};

export const isCustomerReceiptTxn = (txn: BankTransactionRow): boolean =>
  String(txn?.txnType || '').toLowerCase() === 'receipt' &&
  String(txn?.receiptCategory || '').toLowerCase() === 'customer';

export const isCustomerRefundTxn = (txn: BankTransactionRow): boolean =>
  String(txn?.txnType || '').toLowerCase() === 'payment' &&
  String(txn?.paymentCategory || '').toLowerCase() === 'customer_refund';

export const isGstReceiptTxn = (txn: BankTransactionRow): boolean =>
  String(txn?.txnType || '').toLowerCase() === 'receipt' &&
  String(txn?.receiptCategory || txn?.receiptFor || '').toLowerCase().trim() === 'gst';

export const isExcludedFromBookingReport = (txn: BankTransactionRow): boolean => txn.excludeFromBookingReport === true;

export type ActiveBookingReceipt = { txn: BankTransactionRow; unitId: string; ms: number; amount: number };

/**
 * Customer receipts (positive) and refunds (negative) that belong to each unit's active booking.
 * A row without a bookingId counts when the unit only ever had one booking; otherwise it must be
 * dated on or after the active booking was created.
 */
export function activeBookingCustomerReceipts(
  txns: BankTransactionRow[],
  units: ActiveBookingUnit[],
  bookingMetaByUnitId: Map<string, BookingMeta>,
  kind: 'customer' | 'gst' = 'customer'
): ActiveBookingReceipt[] {
  const activeBookingByUnitId = new Map<string, string>();
  units.forEach((row) => {
    const unitId = String(row.id || '').trim();
    const activeBookingId = String(row.activeBookingId || '').trim();
    if (unitId && activeBookingId) activeBookingByUnitId.set(unitId, activeBookingId);
  });
  const out: ActiveBookingReceipt[] = [];
  txns.forEach((row) => {
    if (isExcludedFromBookingReport(row)) return;
    const isReceipt = kind === 'gst' ? isGstReceiptTxn(row) : isCustomerReceiptTxn(row);
    const isRefund = kind === 'customer' && isCustomerRefundTxn(row);
    if (!isReceipt && !isRefund) return;
    const unitId = String(row?.unitId || (kind === 'gst' ? row?.unitID : '') || '').trim();
    if (!unitId) return;
    const activeBookingId = String(activeBookingByUnitId.get(unitId) || '').trim();
    if (!activeBookingId) return;
    const txnBookingId = String(row?.bookingId || '').trim();
    if (txnBookingId && txnBookingId !== activeBookingId) return;
    const txnDate = resolveTxnDateLike(row);
    const ms = txnDate ? txnDate.getTime() : 0;
    if (!txnBookingId) {
      const meta = bookingMetaByUnitId.get(unitId);
      if (Number(meta?.bookingHistoryCount || 0) !== 1) {
        const startMs = Number(meta?.bookingCreatedAtMs || 0);
        if (startMs > 0 && ms > 0 && ms < startMs) return;
      }
    }
    if (!ms) return;
    const amount = Number(row?.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) return;
    out.push({ txn: row, unitId, ms, amount: isReceipt ? amount : -amount });
  });
  return out;
}

/** Net customer receipts per unit for the active booking, dated within [startMs, endMs]. */
export function sumActiveBookingReceiptsByUnit(
  txns: BankTransactionRow[],
  units: ActiveBookingUnit[],
  bookingMetaByUnitId: Map<string, BookingMeta>,
  startMs: number = Number.NEGATIVE_INFINITY,
  endMs: number = Number.POSITIVE_INFINITY
): Map<string, number> {
  const next = new Map<string, number>();
  activeBookingCustomerReceipts(txns, units, bookingMetaByUnitId).forEach((row) => {
    if (row.ms < startMs || row.ms > endMs) return;
    next.set(row.unitId, Number(next.get(row.unitId) || 0) + row.amount);
  });
  return next;
}
